import React, { useState } from 'react';
import useNetworkTest from '../../hooks/useNetworkTest';
import { verifyNetwork } from '../../../ai-interview/utils/aiInterviewApi';

/** Network quality check: latency, jitter, packet loss → rating → store. */
const RATING_TONE = {
  Excellent: 'text-emerald-600 dark:text-emerald-300',
  Good: 'text-emerald-600 dark:text-emerald-300',
  Fair: 'text-amber-600 dark:text-amber-300',
  Poor: 'text-rose-600 dark:text-rose-300',
};

const Metric = ({ label, value }) => (
  <div className="rounded-xl bg-slate-100 px-4 py-3 text-center dark:bg-white/5">
    <p className="text-lg font-bold">{value}</p>
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
  </div>
);

const NetworkTest = ({ ids, onDone }) => {
  const { run, running, progress, result } = useNetworkTest();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const start = async () => { setErr(''); await run(); };

  const submit = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await verifyNetwork(ids.companyId, ids.sessionId, ids.candidateId, result);
      onDone({ ok: true, result });
    } catch (e) { setErr(e.message || 'Could not save the result.'); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-xl text-center">
      {!result && !running && (
        <button type="button" onClick={start}
          className="rounded-full bg-primary-600 px-6 py-3 text-sm font-medium text-white hover:bg-primary-700 dark:bg-violet-600 dark:hover:bg-violet-500">
          Run network test
        </button>
      )}

      {running && (
        <div>
          <div className="mx-auto h-3 max-w-sm overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-[width]" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Testing your connection… {progress}%</p>
        </div>
      )}

      {result && (
        <div>
          <p className={`text-2xl font-extrabold ${RATING_TONE[result.rating] || ''}`}>{result.rating}</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Metric label="Ping" value={result.ping_ms != null ? `${result.ping_ms} ms` : '—'} />
            <Metric label="Jitter" value={result.jitter_ms != null ? `${result.jitter_ms} ms` : '—'} />
            <Metric label="Loss" value={`${result.packet_loss_pct}%`} />
          </div>
          {err && <p className="mt-2 text-xs text-rose-500">{err}</p>}
          <div className="mt-5 flex justify-center gap-3">
            <button type="button" onClick={start} disabled={busy}
              className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5">
              Re-test
            </button>
            <button type="button" onClick={submit} disabled={busy}
              className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkTest;
